import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-simple-toasts";
import { firebase, functions, firestore } from "../firebaseConfig";
import {
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import { CheckCircleOutline, CloseOutlined } from "@mui/icons-material";
import { COLORS } from "../utils/COLORS";
import { httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom"; // Add this import at the top of your file

const ADMIN_PASSWORD = "pledgefast2025"; // Hardcoded password

const AdminPage = () => {
  const [data, setData] = useState({});
  const [avPets, setAvPets] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);

  const [requiresCapturePledges, setRequiresCapturePledges] = useState([]);
  const [unprocessedClaims, setUnprocessedClaims] = useState([]);

  useEffect(() => {
    // Check localStorage for admin access
    const adminAccess = localStorage.getItem('adminAccess');
    if (adminAccess === 'true') {
      setHasAccess(true);
      fetchAllData();
    }
  }, []);

  const fetchAllData = async () => {
    try {
      
      // Fetch claims from Firestore
      const claimsSnapshot = await firestore.collection("claims").get();
      const allClaims = [];
      claimsSnapshot.forEach(doc => {
        allClaims.push({ id: doc.id, ...doc.data() });
      });

      const pendingClaims = allClaims.filter(
        (claim) => claim?.claimStatus === "pending"
      );

      // Fetch pledges from Firestore by going through pledge collections
      const dogsSnapshot = await firestore.collection("web-available-dogs").get();
      
      const allPledges = [];
      
      // Get pledge slugs from pending claims and fetch their pledges directly
      const claimSlugs = [...new Set(pendingClaims.map(claim => claim.slug))]; // Get unique slugs
      console.log(`🔥 CHECKING CLAIM SLUGS:`, claimSlugs);
      
      for (const slug of claimSlugs) {
        try {
          const searchPath = `pledges/${slug}/pledges`;
          console.log(`🔥 SEARCHING PATH: ${searchPath}`);
          const pledgesSnapshot = await firestore.collection(searchPath).get();
          console.log(`🔥 FOUND ${pledgesSnapshot.docs.length} documents at ${searchPath}`);
          
          pledgesSnapshot.forEach(pledgeDoc => {
            const pledgeData = { id: pledgeDoc.id, ...pledgeDoc.data() };
            allPledges.push(pledgeData);
          });
        } catch (error) {
          console.error(`🔥 ERROR accessing search path:`, error);
        }
      }

      const capturePledges = allPledges.filter(
        (pledge) => pledge?.status === "requires_capture"
      );

      setRequiresCapturePledges(capturePledges);

      // Fetch available pets from Firestore
      const availablePets = {};
      dogsSnapshot.forEach(doc => {
        availablePets[doc.id] = { id: doc.id, ...doc.data() };
      });
      setAvPets(availablePets);

      const matchedPledgesAndClaims = pendingClaims.map((claim) => {
        const matchedPledges = capturePledges.filter(
          (pledge) => {
            return pledge.slug === claim.slug;
          }
        );
        return { claim, pledges: matchedPledges };
      });

      // Get pet profiles for matched claims
      const availablePetProfiles = await Promise.all(
        matchedPledgesAndClaims.map(async ({ claim }) => {
          const petDoc = await firestore.collection("web-available-dogs").where("slug", "==", claim.slug).limit(1).get();
          const result = petDoc.empty ? null : { id: petDoc.docs[0].id, ...petDoc.docs[0].data() };
          return result;
        })
      );

      // Fetch subscriptions and match to claims
      const subscriptionsSnapshot = await firestore.collection("subscriptions").get();
      const allSubscriptions = [];
      subscriptionsSnapshot.forEach(doc => {
        const subscriptionData = { id: doc.id, ...doc.data() };
        if (subscriptionData.status === "trialing" || subscriptionData.status === "active") {
          allSubscriptions.push(subscriptionData);
        }
      });

      // Combine matched pledges and claims with available pet profiles and subscriptions
      const combinedData = matchedPledgesAndClaims.map(
        (item, index) => {
          const matchedSubscriptions = allSubscriptions.filter(
            (subscription) => subscription.dogSlug === item.claim.slug
          );
          return {
            ...item,
            petProfile: availablePetProfiles[index],
            subscriptions: matchedSubscriptions,
          };
        }
      );

      setUnprocessedClaims(combinedData);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast("Failed to fetch data. Please try again.");
    }
  };


  const handleSignIn = async () => {
    try {
      if (password === ADMIN_PASSWORD) {
        localStorage.setItem('adminAccess', 'true');
        setHasAccess(true);
        toast("Admin access granted!");
        await fetchAllData();
      } else {
        toast("Incorrect password. Please try again.");
      }
    } catch (error) {
      console.error("Error signing in:", error);
      toast("An error occurred. Please try again.");
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('adminAccess');
    setHasAccess(false);
    setPassword("");
    toast("Signed out successfully");
  };

  const handleAcceptClaim = async (petId, claimId, claim) => {
    try {
      const pet =
        avPets && Object.values(avPets).find((pet) => pet.id === petId);

      // If pet is not found but we have the claim slug, we can still process the claim
      if (!pet && !petId.startsWith('missing-')) {
        console.error("Pet not found for petId:", petId);
        toast("Pet not found");
        return;
      }

      // For missing pets, use claim data
      const petSlug = pet?.slug || claim.slug;

      // Process each pledge for the pet - filter pledges for this specific claim
      const pledges = requiresCapturePledges.filter(pledge => pledge.slug === claim.slug);

      for (const pledge of pledges) {
        if (pledge.status === "requires_capture") {
          try {
            console.log('Calling processPayment with data:', {
              orderId: pledge?.orderId,
              total: pledge.displayAmount,
              customerId: pledge.customerId,
              paymentIntentId: pledge.paymentIntentId,
              vendorStripeAccountId: claim.claimedStripe,
              uid: claim.claimedBy,
              dogSlug: pledge?.dogSlug,
            });
            
            const processPayment = httpsCallable(functions, "processPayment");
            const paymentResult = await processPayment({
              orderId: pledge?.orderId,
              total: pledge.totalAmount || pledge.displayAmount, // Capture full amount including tip
              transferAmount: pledge.displayAmount, // But only transfer pledge amount (no tip)
              customerId: pledge.customerId,
              paymentIntentId: pledge.paymentIntentId,
              vendorStripeAccountId: claim.claimedStripe,
              uid: claim.claimedBy,
              dogSlug: pledge?.dogSlug,
            });
            

            if (!paymentResult?.data) {
              throw new Error(`Payment function returned null data for pledge ${pledge?.orderId}`);
            }
            
            if (!paymentResult.data.success) {
              throw new Error(
                paymentResult.data.error ||
                  `Failed to process payment for pledge ${pledge?.orderId}`
              );
            }
          } catch (paymentError) {
            console.error(
              `Error processing payment for pledge ${pledge?.orderId}:`,
              paymentError
            );
            // You might want to handle this error differently, e.g., continue processing other pledges
            // For now, we'll throw the error to stop the process
            throw paymentError;
          }
        } else {
          console.log(`Pledge ${pledge?.orderId} does not require capture, skipping`);
        }
      }

      console.log("All pledges processed, updating pet status");

      // Update the pet's claim status in Firestore (only if pet exists)
      if (pet) {
        const petDoc = await firestore.collection("web-available-dogs").where("slug", "==", petSlug).limit(1).get();
        if (!petDoc.empty) {
          await firestore.collection("web-available-dogs").doc(petDoc.docs[0].id).update({
            claimStatus: "accepted",
            euthanasiaStatus: "rescued",
          });
        }
      }

      // update the claims
      await firestore.collection("claims").doc(claimId).update({
        claimStatus: "accepted",
      });

      // Iterate through all pledges and update their status - filter pledges for this specific claim
      const claimPledges = requiresCapturePledges.filter(pledge => pledge.slug === claim.slug);
      for (const pledge of claimPledges) {
        // Update pledge status in the central pledges collection (single source of truth)
        await firestore.collection(`pledges/${petSlug}/pledges`).doc(pledge?.orderId).update({
          status: "processed",
          rescueDate: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }

      // Update local state
      setAvPets((prevPets) => ({
        ...prevPets,
        [petId]: {
          ...prevPets[petId],
          claimStatus: "accepted",
          euthanasiaStatus: "rescued",
        },
      }));

      console.log("Claim accepted and pledges processed successfully");
      toast("Claim accepted and pledges processed successfully");
    } catch (error) {
      console.error("Error accepting claim:", error);
      toast("Failed to accept claim. Please try again.");
    }
  };

  const handleDenyClaim = (petId) => {
    // TODO: Implement deny claim logic
    console.log("Denied claim for pet:", petId);
    toast("Claim denied");
  };

  const calculateTotalPledged = (pledges) => {
    if (!pledges || !Array.isArray(pledges)) return 0;
    return pledges.reduce((total, pledge) => {
      if (pledge.status === "requires_capture") {
        return total + (pledge.displayAmount || 0);
      }
      return total;
    }, 0);
  };

  if (!hasAccess) {
    return (
      <Container>
        <Title>PledgeFast Admin</Title>
        <InputContainer>
          <Input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSignIn()}
          />
          <LoginButton onClick={handleSignIn}>ACCESS ADMIN</LoginButton>
        </InputContainer>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#000000", minHeight: "100vh", color: "#FFFFFF" }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, backgroundColor: "transparent" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: "#FFFFFF", fontWeight: "600" }}>
            Admin Dashboard
          </Typography>
          <SignOutButton onClick={handleSignOut}>
            Sign Out
          </SignOutButton>
        </Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "#FFFFFF", fontWeight: "500" }}>
        Pending Claims
      </Typography>
      {(() => {
        return null;
      })()}
      {unprocessedClaims && unprocessedClaims.length > 0 ? (
        <Grid container spacing={3}>
          {unprocessedClaims.map((data, index) => {
            const pet = data.petProfile;
            const claim = data.claim;
            const pledges = data.pledges;
            const subscriptions = data.subscriptions || [];

            // Handle cases where pet profile is missing
            const petDisplayName = pet?.name || `Unknown Pet (${claim.slug})`;
            const petId = pet?.id || `missing-${claim.slug}`;
            const petBreed = pet?.breed || "Unknown breed";
            const petAge = pet?.age || "Unknown age";

            return (
              <Grid item xs={12} key={petId}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, backgroundColor: "#1F2937", border: "1px solid #374151" }}>
                  <Typography
                    variant="h6"
                    component={pet ? Link : "div"}
                    to={pet ? `https://pledgefast.com/${pet.slug}` : undefined}
                    target={pet ? "_blank" : undefined}
                    rel={pet ? "noopener noreferrer" : undefined}
                    sx={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                      color: pet ? "#FFFFFF" : "#EF4444",
                      textDecoration: "none",
                      "&:hover": { color: pet ? "#10B981" : "#EF4444" },
                    }}
                  >
                    {petDisplayName}{" "}
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, color: "#9CA3AF" }}
                    >
                      (#{pet?.animalId || claim.animalId})
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, color: "#9CA3AF" }}
                    >
                      ({pledges ? pledges.length : 0} pledges, {subscriptions.length} subscriptions)
                    </Typography>
                  </Typography>

                  <Typography
                    variant="body2"
                    gutterBottom
                    sx={{ color: "#D1D5DB" }}
                  >
                    {petBreed} • {petAge} years old
                  </Typography>

                  <Box sx={{ borderTop: "1px solid #374151", mt: 2, pt: 2 }}>
                    {/* Pledges */}
                    {pledges &&
                      pledges.map(
                        (pledge, index) => (
                          <Box
                            key={pledge.orderId || index}
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                mb: 1,
                                mt: 1,
                                fontSize: "14px",
                                color: "#10B981",
                                fontWeight: "600",
                              }}
                            >
                              ${pledge.displayAmount}
                            </Typography>
                            <Typography
                              sx={{
                                mb: 1,
                                mt: 1,
                                fontSize: "14px",
                                color: "#9CA3AF",
                              }}
                            >
                              {new Date(pledge.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                        )
                      )}
                    
                    {/* Subscriptions */}
                    {subscriptions &&
                      subscriptions.map(
                        (subscription, index) => (
                          <Box
                            key={subscription.id || index}
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                mb: 1,
                                mt: 1,
                                fontSize: "14px",
                                color: "#F59E0B",
                                fontWeight: "600",
                              }}
                            >
                              ${subscription.amount}/mo
                            </Typography>
                            <Typography
                              sx={{
                                mb: 1,
                                mt: 1,
                                fontSize: "14px",
                                color: "#9CA3AF",
                              }}
                            >
                              {subscription.createdAt ? new Date(subscription.createdAt.toDate()).toLocaleString() : 'N/A'}
                            </Typography>
                          </Box>
                        )
                      )}
                  </Box>

                  {/* Display the single claim we have for this item */}
                  <Box key={claim.id} sx={{ mt: 2, pt: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Typography
                          sx={{
                            mb: 1,
                            mt: 1,
                            fontSize: "14px",
                            color: "#D1D5DB",
                          }}
                        >
                          Claim ID: {claim.id}
                        </Typography>
                        <Typography
                          sx={{
                            mb: 1,
                            mt: 1,
                            fontSize: "14px",
                            color: "#D1D5DB",
                          }}
                        >
                          Claimed By: {claim.claimedBy}
                        </Typography>
                        <Typography
                          sx={{
                            mb: 1,
                            mt: 1,
                            fontSize: "14px",
                            color: "#D1D5DB",
                          }}
                        >
                          Claimed At:{" "}
                          {new Date(claim.claimedAt?.toDate ? claim.claimedAt.toDate() : claim.claimedAt).toLocaleString()}
                          ({Math.floor(
                            (Date.now() - (claim.claimedAt?.toDate ? claim.claimedAt.toDate().getTime() : claim.claimedAt)) / 3600000
                          )}{" "}
                          hours ago)
                        </Typography>
                        
                        {/* Financial Insights */}
                        <Typography
                          sx={{
                            mb: 1,
                            mt: 2,
                            fontSize: "16px",
                            color: "#10B981",
                            fontWeight: "600",
                          }}
                        >
                          💰 Financial Overview:
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: "13px", color: "#D1D5DB" }}>
                          • Pledges: {pledges ? pledges.length : 0} pledges totaling ${calculateTotalPledged(pledges).toLocaleString()}
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: "13px", color: "#D1D5DB" }}>
                          • Average Pledge: ${pledges && pledges.length > 0 ? (calculateTotalPledged(pledges) / pledges.length).toFixed(2) : '0.00'}
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: "13px", color: "#D1D5DB" }}>
                          • Subscriptions: {subscriptions.length} active subs totaling ${subscriptions.reduce((sum, sub) => sum + (sub.amount || 0), 0).toLocaleString()}/mo
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: "13px", color: "#D1D5DB" }}>
                          • Avg Subscription: ${subscriptions.length > 0 ? (subscriptions.reduce((sum, sub) => sum + (sub.amount || 0), 0) / subscriptions.length).toFixed(2) : '0.00'}/mo
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: "13px", color: COLORS.primaryGreen, fontWeight: "600" }}>
                          • Tips from Pledges: ${(calculateTotalPledged(pledges) * 0.15).toFixed(2)}
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: "13px", color:COLORS.primaryGreen, fontWeight: "600" }}>
                          • 15% of Monthly Subs: ${(subscriptions.reduce((sum, sub) => sum + (sub.amount || 0), 0) * 0.15).toFixed(2)}/mo
                        </Typography>
                        <Typography sx={{ mb: 1, fontSize: "13px", color: COLORS.primaryGreen, fontWeight: "600" }}>
                          • Total Potential Earnings: ${(calculateTotalPledged(pledges) + (subscriptions.reduce((sum, sub) => sum + (sub.amount || 0), 0) * 0.15)).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                          }}
                        >
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to accept this claim?"
                                )
                              ) {
                                handleAcceptClaim(petId, claim.id, claim);
                              }
                            }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              handleDenyClaim(petId, claim.id)
                            }
                          >
                            Deny
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: "#FFFFFF", fontWeight: "600" }}>
                        Proof of Rescue:
                      </Typography>
                      <img
                        src={claim.proofOfRescueUrl}
                        alt="Proof of Rescue"
                        style={{
                          width: "100%",
                          maxHeight: "300px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, backgroundColor: "#1F2937", border: "1px solid #374151" }}>
          <Typography variant="h6" align="center" sx={{ color: "#FFFFFF" }}>
            No pending claims
          </Typography>
        </Paper>
      )}

      </Container>
    </Box>
  );
};

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  background-color: #000000;
  color: #FFFFFF;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 32px;
  color: #FFFFFF;
  text-align: center;
`;

const InputContainer = styled.div`
  margin-bottom: 24px;
  width: 100%;
  max-width: 400px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #1E293B;
  border: 1px solid #334155;
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 14px;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #10B981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  }
`;

const LoginButton = styled(Button)`
  background-color: #10B981;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  text-transform: none;
  width: 100%;
  
  &:hover {
    background-color: #059669;
    transform: translateY(-1px);
  }
`;

const SignOutButton = styled(Button)`
  background-color: #DC2626;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  text-transform: none;
  margin-bottom: 16px;
  
  &:hover {
    background-color: #B91C1C;
  }
`;

export default AdminPage;
